import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseComponent } from '@base/base.component';
import { Period } from '@models/period';
import { Paginator, PaginatorItems, Table } from '@models/table';
import { AppService } from '@services/app.service';
import { ReportsService } from '@services/reports.service';
import { firstValueFrom } from 'rxjs';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { saveAs } from "file-saver-es";
import { PERMITION_CONSORTIUM_SALES_PANEL_EXPORT_PDF, PERMITION_CONSORTIUM_SALES_PANEL_EXPORT_XLSX } from 'src/app/const/permitions';
import { formatNumberToLocal } from 'src/app/utils/format-number-to-local';

@Component({
  selector: 'app-sales-details-modal',
  templateUrl: './sales-details-modal.component.html',
  styleUrl: './sales-details-modal.component.scss'
})
export class SalesDetailsModalComponent extends ModalComponent<SalesDetailsModalComponent> {
  private readonly _permissions: string[];
  public readonly table: Table<any> = this.createTable();
  public readonly hasAccessToAnyDealer: boolean;
  private hasDoneFirstSearch: boolean = false;


  public loading: boolean = false;

  public readonly plataform: string;
  private readonly _identifierCode: string | null;
  private readonly _firstPayedInstallmentPeriod: Period | null;

  public isExportingExcel: boolean = false;
  public isExportingPdf: boolean = false;

  public constructor(private readonly _datePipe: DatePipe,
                     private readonly _reportsService: ReportsService,
                     @Inject(AppService) appService: AppService,
                     @Inject(MAT_DIALOG_DATA) public readonly matDialogData,
  ) {
    super();

    this._identifierCode = matDialogData.identifierCode;
    this._firstPayedInstallmentPeriod = matDialogData.firstPayedInstallmentPeriod;
    this._permissions = appService.getPermissions();
    this.plataform = matDialogData.plataform;

    this.hasAccessToAnyDealer = appService.getAllAllowedDealers().length > 0;
  }

  public ngOnInit() {
    this.filter();
  }


  public filter(paginator?: Paginator): void {
    if (!this.hasAccessToAnyDealer) {
      return;
    }

    this.loading = true;
    if (!paginator) {
      paginator = this.table.paginator;
    } else {
      this.table.paginator = paginator;
    }

    firstValueFrom(
      this._reportsService.getSalesDetailsReport({
        firstPayedInstallmentPeriod: this._firstPayedInstallmentPeriod,
        identifierCode: this._identifierCode,
        plataform: this.plataform
      }, paginator, this.hasDoneFirstSearch)
    )
      .then((resp: PaginatorItems<any>) => {
        this.table.data = resp.items;
        this.table.paginator.length = resp.totalCount;
        this.table.paginator.pageSize = paginator.pageSize;
      })
      .finally(() => {
        this.loading = false;
        this.hasDoneFirstSearch = true;
      });
  }

  public pageChange(paginator: Paginator): void {
    this.filter(paginator);
  }

  public get hasPermissionToExportExcel(): boolean {
    return this._permissions.some(p => [PERMITION_CONSORTIUM_SALES_PANEL_EXPORT_XLSX].includes(p));
  }

  public get hasPermissionToExportPdf(): boolean {
    return this._permissions.some(p => [PERMITION_CONSORTIUM_SALES_PANEL_EXPORT_PDF].includes(p));
  }

  public async exportPdfReport(): Promise<void> {
    try {
      this.isExportingPdf = true;
      const result = await firstValueFrom(this._reportsService.exportSalesDetailsToPdf({
        identifierCode: this._identifierCode,
        firstPayedInstallmentPeriod: this._firstPayedInstallmentPeriod,
        plataform: this.plataform
      }));

      this.download(result, "relatorio-vendas.pdf", "application/pdf");
    } finally {
      this.isExportingPdf = false;
    }
  }

  public async exportWorksheetReport(): Promise<void> {
    try {
      this.isExportingExcel = true;
      const result = await firstValueFrom(this._reportsService.exportSalesDetailsToXlsx({
        identifierCode: this._identifierCode,
        firstPayedInstallmentPeriod: this._firstPayedInstallmentPeriod,
        plataform: this.plataform
      }));

      this.download(result.fileBase64, result.fileName, result.contentType);
    } finally {
      this.isExportingExcel = false;
    }
  }

  public download(
    data: string | Blob,
    name: string,
    contentType: string
  ): void {
    let blob: Blob;

    if (!(data instanceof Blob)) {
      blob = this.base64toBlob(data, contentType);
    } else {
      blob = data;
    }

    var file = new File([blob], name, { type: "application/octet-stream" });
    saveAs(file, name);
  }

  private base64toBlob(base64Data: string, contentType: string) {
    const sliceSize = 512;
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  private createTable(): Table<any> {
    return {
      loading: false,
      headers: [
          {
            key: "contract",
            value: "Contrato",
          },
          {
            key: "situation",
            value: "Situação",
          },
          {
            key: "group",
            value: "Grupo",
          },
          {
            key: "quota",
            value: "Cota",
          },
          {
            key: "name",
            value: "Nome",
          },
          {
            key: "phone",
            value: "Telefone",
          },
          {
            key: "email",
            value: "E-mail",
          },
          {
            key: "asset",
            value: "Bem",
          },
          {
            key: "credit",
            value: "Crédito",
          },
          {
            key: "dueDateDays",
            value: "Prazo",
          },
          {
            key: "value",
            value: "Valor",
          },
          {
            key: "salesperson",
            value: "Vendedor",
          },
      ],
      data: [],
      style: {
        table: {
          height: "500px",
        },
        data: [
          {
            key: "saleDate",
            transform: (value) =>
              this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "dueDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "cancellationDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "contemplationDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "deliveredAt",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "groupClosureDate",
            transform: (value) => value && this._datePipe.transform(value, "dd/MM/yyyy"),
          },
          {
            key: "asset",
            transform: (value) => value && formatNumberToLocal(value),
          },
          {
            key: "credit",
            transform: (value) => value && formatNumberToLocal(value),
          },
          {
            key: "value",
            transform: (value) => value && formatNumberToLocal(value),
          },
        ]
      },
      checkbox: false,
      actions: false,
      paginator: {
        index: 1,
        itensPerPage: [10, 15, 20, 50, 100],
        pageSize: 10,
      },
      messageWithoutDates: "Não há dados para exibir no momento.",
    };
  }
}
